import React, { useEffect, useRef, useState } from 'react';
import { agentName } from 'shared/branding';
import AgentAvatar, { agentStatus } from 'features/agents/AgentAvatar';

const SIZE = 56;
const OFFSET = 16;
const CORNERS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
const closestCorner = (x, y) => `${y < window.innerHeight / 2 ? 'top' : 'bottom'}-${x < window.innerWidth / 2 ? 'left' : 'right'}`;
const anchor = (corner) => ({
  left: corner.includes('left') ? OFFSET : window.innerWidth - SIZE - OFFSET,
  top: corner.includes('top') ? OFFSET : window.innerHeight - SIZE - OFFSET,
});

/** The original draggable bubble and floating frame, with the current agent conversation inside. */
export default function FloatingConversation({ panel, fullscreen, children }) {
  const [corner, setCorner] = useState('bottom-right');
  const [drag, setDrag] = useState(null);
  const [position, setPosition] = useState(null);
  const [, resize] = useState(0);
  const moved = useRef(false);
  const suppressClick = useRef(false);
  const snapTimer = useRef(null);
  const launcherRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    const onResize = () => resize((value) => value + 1);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.clearTimeout(snapTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!drag) return undefined;
    const move = (event) => {
      if (Math.abs(event.clientX - drag.x) > 4 || Math.abs(event.clientY - drag.y) > 4) moved.current = true;
      setPosition({
        left: Math.max(0, Math.min(event.clientX - drag.offsetX, window.innerWidth - SIZE)),
        top: Math.max(0, Math.min(event.clientY - drag.offsetY, window.innerHeight - SIZE)),
      });
    };
    const finish = (event) => {
      const next = moved.current ? closestCorner(event.clientX, event.clientY) : corner;
      setCorner(next);
      suppressClick.current = moved.current;
      setDrag(null);
      if (moved.current) {
        setPosition(anchor(next));
        snapTimer.current = window.setTimeout(() => setPosition(null), 320);
      } else setPosition(null);
    };
    const cancel = () => {
      suppressClick.current = true;
      setDrag(null);
      setPosition(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', cancel);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', cancel);
    };
  }, [drag, corner]);

  useEffect(() => {
    if (panel.isPanelOpen) frameRef.current?.querySelector('textarea')?.focus();
  }, [panel.isPanelOpen]);

  const title = agentName(panel.agent);
  const label = `Open conversation with ${title}`;
  const activity = panel.feed?.agents?.[panel.agent?.id] || panel.agent?.activity;
  const status = agentStatus(panel.agent, activity);
  const needsYou = Number(panel.needsYouCount || 0);
  const hasUpdates = Number(panel.unseenUpdatesCount || 0) > 0 || panel.unreadAgentIds?.size > 0;
  const notification = needsYou > 0 ? 'needs-you' : hasUpdates ? 'updates' : 'none';
  const notificationRing = needsYou > 0 ? 'ring-2 ring-amber-400' : hasUpdates ? 'ring-2 ring-cyan-500' : 'ring-1 ring-gray-200/80';
  const nearest = drag && position ? closestCorner(position.left + SIZE / 2, position.top + SIZE / 2) : null;
  return (
    <>
      {drag ? CORNERS.map((target) => (
        <div key={target} aria-hidden className={`pointer-events-none fixed z-40 h-14 w-14 rounded-full border-2 border-dashed transition-all ${target === nearest ? 'scale-110 border-sky-400 bg-sky-400/10' : 'border-gray-300/70'}`} style={anchor(target)} />
      )) : null}
      {!panel.isPanelOpen ? (
        <button
          ref={launcherRef}
          type="button"
          aria-label={label}
          title={`Ask ${title}${needsYou > 0 ? ` · ${needsYou} waiting on you` : hasUpdates ? ' · New agent updates' : ''}`}
          data-testid="conversation-launcher"
          data-notification-state={notification}
          style={position || anchor(corner)}
          className={`group fixed z-50 flex h-14 w-14 touch-none select-none items-center justify-center rounded-full bg-white ${notificationRing} ${drag ? 'scale-95 cursor-grabbing shadow-md' : 'cursor-grab shadow-lg shadow-slate-900/15 transition-all duration-300 hover:scale-105 hover:shadow-xl'}`}
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            window.clearTimeout(snapTimer.current);
            moved.current = false;
            suppressClick.current = false;
            const rect = event.currentTarget.getBoundingClientRect();
            setDrag({ x: event.clientX, y: event.clientY, offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top });
            setPosition({ left: rect.left, top: rect.top });
          }}
          onClick={() => {
            if (suppressClick.current) {
              suppressClick.current = false;
              return;
            }
            panel.setIsPanelOpen(true);
          }}
        >
          <AgentAvatar ui={panel.agent?.ui} size="queue" status={status} label={title} />
          {needsYou > 0 ? (
            <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-amber-500 px-1 text-center text-[10px] font-bold leading-[18px] text-white" data-testid="needs-you-badge" aria-label={`${needsYou} waiting on you`}>
              {needsYou > 99 ? '99+' : needsYou}
            </span>
          ) : hasUpdates ? <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-cyan-500 ring-2 ring-white" aria-label="New agent updates" /> : null}
        </button>
      ) : (
        <div
          ref={frameRef}
          role="dialog"
          aria-label={`Conversation with ${title}`}
          data-testid="floating-conversation"
          onKeyDown={(event) => {
            // Let an open menu consume Escape before closing the conversation.
            if (event.key === 'Escape' && !event.defaultPrevented && !frameRef.current?.querySelector('[aria-expanded="true"]')) {
              panel.setIsPanelOpen(false);
              window.requestAnimationFrame(() => launcherRef.current?.focus());
            }
          }}
          className={`fixed z-50 flex flex-col overflow-hidden border border-gray-200/70 bg-white/95 shadow-2xl shadow-slate-900/15 ring-1 ring-black/5 backdrop-blur-xl ${fullscreen ? 'inset-0 rounded-none' : 'rounded-2xl'}`}
          style={fullscreen ? undefined : {
            width: 'min(560px, calc(100vw - 32px))',
            height: 'min(720px, calc(100dvh - 32px))',
            [corner.includes('left') ? 'left' : 'right']: OFFSET,
            [corner.includes('top') ? 'top' : 'bottom']: OFFSET,
          }}
        >
          {children}
        </div>
      )}
    </>
  );
}
